/*
 * Cart-page shipping address with Google Places autocomplete (Mexico).
 *
 * Flow: customer searches -> picks a Google suggestion -> fields are filled (editable)
 * -> address is stored on the cart with the Storefront API (tokenless) via
 * cartDeliveryAddressesReplace -> Shopify checkout opens with it prefilled.
 *
 * Never blocks a sale on our own failure: if Google or the Storefront API fails,
 * checkout continues and the customer types the address there.
 */
(function () {
  'use strict';

  var cfg = window.mxAddressConfig || {};
  if (!cfg.enabled) return;

  var API_URL = '/api/2026-04/graphql.json';
  var SKIP_KEY = 'mxaddr-skip';
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var STATES = {
    AGS: 'Aguascalientes', BC: 'Baja California', BCS: 'Baja California Sur', CAMP: 'Campeche',
    CHIS: 'Chiapas', CHIH: 'Chihuahua', DF: 'Ciudad de México', COAH: 'Coahuila', COL: 'Colima',
    DGO: 'Durango', GTO: 'Guanajuato', GRO: 'Guerrero', HGO: 'Hidalgo', JAL: 'Jalisco',
    MEX: 'Estado de México', MICH: 'Michoacán', MOR: 'Morelos', NAY: 'Nayarit', NL: 'Nuevo León',
    OAX: 'Oaxaca', PUE: 'Puebla', QRO: 'Querétaro', 'Q ROO': 'Quintana Roo', SLP: 'San Luis Potosí',
    SIN: 'Sinaloa', SON: 'Sonora', TAB: 'Tabasco', TAMPS: 'Tamaulipas', TLAX: 'Tlaxcala',
    VER: 'Veracruz', YUC: 'Yucatán', ZAC: 'Zacatecas'
  };
  // Google's state names/abbreviations -> Shopify province codes
  var STATE_ALIASES = {
    'ciudad de mexico': 'DF', 'cdmx': 'DF', 'cd. de mexico': 'DF', 'distrito federal': 'DF',
    'mexico': 'MEX', 'estado de mexico': 'MEX', 'mex.': 'MEX', 'edo. mex.': 'MEX',
    'coahuila de zaragoza': 'COAH', 'michoacan de ocampo': 'MICH',
    'veracruz de ignacio de la llave': 'VER', 'queretaro de arteaga': 'QRO',
    'ags.': 'AGS', 'b.c.': 'BC', 'b.c.s.': 'BCS', 'camp.': 'CAMP', 'chis.': 'CHIS', 'chih.': 'CHIH',
    'coah.': 'COAH', 'col.': 'COL', 'dgo.': 'DGO', 'gto.': 'GTO', 'gro.': 'GRO', 'hgo.': 'HGO',
    'jal.': 'JAL', 'mich.': 'MICH', 'mor.': 'MOR', 'nay.': 'NAY', 'n.l.': 'NL', 'oax.': 'OAX',
    'pue.': 'PUE', 'qro.': 'QRO', 'q.r.': 'Q ROO', 'q. roo': 'Q ROO', 's.l.p.': 'SLP', 'sin.': 'SIN',
    'son.': 'SON', 'tab.': 'TAB', 'tamps.': 'TAMPS', 'tlax.': 'TLAX', 'ver.': 'VER', 'yuc.': 'YUC',
    'zac.': 'ZAC'
  };

  function norm(s) {
    return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  }

  function stateCode(longText, shortText) {
    var candidates = [norm(longText), norm(shortText)];
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (!c) continue;
      if (STATE_ALIASES[c]) return STATE_ALIASES[c];
      for (var code in STATES) {
        if (norm(STATES[code]) === c || norm(code) === c) return code;
      }
    }
    return '';
  }

  function skipped() {
    try { return sessionStorage.getItem(SKIP_KEY) === '1'; } catch (e) { return false; }
  }

  /* ---------- Storefront API (cart delivery address) ---------- */

  var MxCart = {
    gql: function (query, variables) {
      return fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ query: query, variables: variables })
      }).then(function (r) { return r.json(); }).then(function (j) {
        if (j.errors && j.errors.length) throw new Error(j.errors[0].message);
        return j.data;
      });
    },

    cartId: function () {
      return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) { return c && c.token && c.item_count ? 'gid://shopify/Cart/' + c.token : null; });
    },

    getAddress: function () {
      return MxCart.cartId().then(function (id) {
        if (!id) return null;
        return MxCart.gql(
          'query($id:ID!){cart(id:$id){delivery{addresses{selected address{... on CartDeliveryAddress{address1 address2 city provinceCode zip countryCode}}}}}}',
          { id: id }
        ).then(function (d) {
          var list = (d.cart && d.cart.delivery && d.cart.delivery.addresses) || [];
          var sel = list.filter(function (a) { return a.selected; })[0] || list[0];
          return sel && sel.address && sel.address.address1 ? sel.address : null;
        });
      });
    },

    save: function (addr) {
      return MxCart.cartId().then(function (id) {
        if (!id) throw new Error('empty cart');
        return MxCart.gql(
          'mutation($id:ID!,$a:[CartSelectableAddressInput!]!){cartDeliveryAddressesReplace(cartId:$id,addresses:$a){userErrors{message}}}',
          {
            id: id,
            a: [{
              selected: true,
              oneTimeUse: true,
              address: { deliveryAddress: {
                address1: addr.address1, address2: addr.address2, city: addr.city,
                provinceCode: addr.provinceCode, zip: addr.zip, countryCode: 'MX'
              } }
            }]
          }
        ).then(function (d) {
          var errs = d.cartDeliveryAddressesReplace.userErrors;
          if (errs && errs.length) throw new Error(errs[0].message);
        });
      });
    }
  };

  /* ---------- <mx-address> ---------- */

  class MxAddress extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.apiKey = this.dataset.apiKey;
      this.reqId = 0;
      this.predictions = [];
      this.active = -1;

      var $ = this.querySelector.bind(this);
      this.steps = {
        search: $('[data-step="search"]'),
        fields: $('[data-step="fields"]'),
        summary: $('[data-step="summary"]')
      };
      this.search = $('#mxAddrSearch');
      this.panel = $('.mx-address__panel');
      this.list = $('#mxAddrList');
      this.errorEl = $('.mx-address__error');
      this.gateEl = $('.mx-address__gate');
      this.summaryText = $('.mx-address__summary-text');
      this.inputs = {};
      this.querySelectorAll('[data-field]').forEach(function (el) { this.inputs[el.dataset.field] = el; }, this);

      // The theme's cart form treats every "change" inside it as a quantity update.
      this.addEventListener('change', function (e) { e.stopPropagation(); });
      // Enter must never submit the cart form (that would go to checkout).
      this.addEventListener('keydown', this.onKeydown.bind(this));

      this.search.addEventListener('focus', this.loadGoogle.bind(this), { once: true });
      this.search.addEventListener('input', this.debounce(this.onSearch.bind(this), 220));
      this.search.addEventListener('blur', function () { setTimeout(this.closePanel.bind(this), 150); }.bind(this));
      this.list.addEventListener('mousedown', function (e) { e.preventDefault(); });
      this.list.addEventListener('click', function (e) {
        var li = e.target.closest('[data-index]');
        if (li) this.choose(+li.dataset.index);
      }.bind(this));
      this.inputs.zip.addEventListener('input', function (e) { e.target.value = e.target.value.replace(/\D/g, '').slice(0, 5); });

      $('.mx-address__manual').addEventListener('click', this.showManual.bind(this));
      $('.mx-address__research').addEventListener('click', this.showSearch.bind(this));
      $('.mx-address__change').addEventListener('click', this.showFields.bind(this));
      $('.mx-address__save').addEventListener('click', function () { this.save(); }.bind(this));
      $('.mx-address__skip').addEventListener('click', this.skip.bind(this));

      this.state = 'search';
      MxCart.getAddress().then(function (a) {
        if (a && a.countryCode === 'MX') {
          this.fill(a);
          this.showSummary();
        }
      }.bind(this)).catch(function () {}).then(function () {
        if (location.hash === '#envio' && this.state === 'search') this.focusSearch();
      }.bind(this));
    }

    debounce(fn, ms) {
      var t;
      return function () { var a = arguments; clearTimeout(t); t = setTimeout(function () { fn.apply(null, a); }, ms); };
    }

    setStep(step) {
      this.state = step;
      for (var k in this.steps) this.steps[k].hidden = k !== step;
      this.gateEl.hidden = true;
    }

    /* Google */

    loadGoogle() {
      if (this._google) return this._google;
      var key = this.apiKey;
      var self = this;
      this._google = new Promise(function (resolve, reject) {
        if (window.google && google.maps && google.maps.importLibrary) return resolve();
        var cb = '__mxAddrGmapsReady';
        window[cb] = function () { resolve(); };
        window.gm_authFailure = function () { self.googleFailed(); };
        var s = document.createElement('script');
        s.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) +
          '&v=weekly&loading=async&language=es&region=MX&callback=' + cb;
        s.async = true;
        s.onerror = function () { reject(new Error('Google Maps failed to load')); };
        document.head.appendChild(s);
      }).then(function () {
        return google.maps.importLibrary('places');
      }).catch(function (err) {
        self.googleFailed();
        throw err;
      });
      return this._google;
    }

    googleFailed() {
      if (this._googleDown) return;
      this._googleDown = true;
      this.closePanel();
      if (this.state === 'search') this.showManual();
    }

    onSearch() {
      var q = this.search.value.trim();
      var id = ++this.reqId;
      if (q.length < 3) return this.closePanel();
      this.loadGoogle().then(function (places) {
        if (!this.session) this.session = new places.AutocompleteSessionToken();
        return places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: q,
          sessionToken: this.session,
          includedRegionCodes: ['mx'],
          includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'route'],
          language: 'es-MX',
          region: 'mx'
        });
      }.bind(this)).then(function (res) {
        if (id !== this.reqId) return;
        this.predictions = (res.suggestions || [])
          .map(function (s) { return s.placePrediction; })
          .filter(Boolean);
        this.renderList();
      }.bind(this)).catch(function () {
        if (id === this.reqId) this.renderStatus('No pudimos buscar direcciones. Usa “No encuentro mi dirección”.');
      }.bind(this));
    }

    renderList() {
      this.active = -1;
      this.list.innerHTML = '';
      if (!this.predictions.length) return this.renderStatus('Sin resultados. Prueba con calle y número, o escribe tu dirección manualmente.');
      this.predictions.forEach(function (p, i) {
        var li = document.createElement('li');
        li.className = 'mx-address__option';
        li.id = 'mxAddrOpt-' + i;
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.dataset.index = i;
        var main = document.createElement('span');
        main.className = 'mx-address__option-main';
        main.textContent = p.mainText ? p.mainText.text : p.text.text;
        var sub = document.createElement('span');
        sub.className = 'mx-address__option-sub';
        sub.textContent = p.secondaryText ? p.secondaryText.text : '';
        li.append(main, sub);
        this.list.appendChild(li);
      }, this);
      this.openPanel();
    }

    renderStatus(msg) {
      this.predictions = [];
      this.list.innerHTML = '';
      var li = document.createElement('li');
      li.className = 'mx-address__option mx-address__option--status';
      li.textContent = msg;
      this.list.appendChild(li);
      this.openPanel();
    }

    openPanel() {
      this.panel.hidden = false;
      this.search.setAttribute('aria-expanded', 'true');
    }

    closePanel() {
      this.panel.hidden = true;
      this.search.setAttribute('aria-expanded', 'false');
      this.search.removeAttribute('aria-activedescendant');
    }

    move(delta) {
      if (!this.predictions.length) return;
      var opts = this.list.querySelectorAll('[role="option"]');
      if (this.active >= 0) opts[this.active].setAttribute('aria-selected', 'false');
      this.active = (this.active + delta + opts.length) % opts.length;
      opts[this.active].setAttribute('aria-selected', 'true');
      opts[this.active].scrollIntoView({ block: 'nearest' });
      this.search.setAttribute('aria-activedescendant', opts[this.active].id);
    }

    onKeydown(e) {
      if (e.target === this.search) {
        var open = !this.panel.hidden;
        if (e.key === 'ArrowDown') { e.preventDefault(); open ? this.move(1) : this.onSearch(); return; }
        if (e.key === 'ArrowUp') { e.preventDefault(); this.move(-1); return; }
        if (e.key === 'Escape') { this.closePanel(); return; }
        if (e.key === 'Enter') {
          e.preventDefault();
          if (open && this.predictions.length) this.choose(this.active >= 0 ? this.active : 0);
          return;
        }
      } else if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
        e.preventDefault();
        this.save();
      }
    }

    choose(i) {
      var p = this.predictions[i];
      if (!p) return;
      this.closePanel();
      this.classList.add('is-busy');
      var place = p.toPlace();
      place.fetchFields({ fields: ['addressComponents'] }).then(function () {
        this.session = null; // a selection ends the billing session
        this.fillFromGoogle(place.addressComponents || [], p);
      }.bind(this)).catch(function () {
        this.showManual();
        this.inputs.street.value = p.mainText ? p.mainText.text : '';
      }.bind(this)).then(function () {
        this.classList.remove('is-busy');
      }.bind(this));
    }

    fillFromGoogle(components, prediction) {
      var get = function (type, short) {
        var c = components.filter(function (x) { return x.types.indexOf(type) !== -1; })[0];
        return c ? (short ? c.shortText : c.longText) : '';
      };
      var stateComp = components.filter(function (x) { return x.types.indexOf('administrative_area_level_1') !== -1; })[0];
      var route = get('route');
      var number = get('street_number');
      var street = route ? route + (number ? ' ' + number : '') : (prediction.mainText ? prediction.mainText.text : '');

      this.fill({
        address1: street,
        address2: get('neighborhood') || get('sublocality_level_1') || get('sublocality'),
        zip: get('postal_code'),
        city: get('locality') || get('administrative_area_level_2'),
        provinceCode: stateComp ? stateCode(stateComp.longText, stateComp.shortText) : ''
      });
      this.showFields();

      if (!/\d/.test(this.inputs.street.value)) {
        this.showError('Agrega tu número exterior a la calle.', ['street']);
        this.inputs.street.focus();
      } else {
        var firstEmpty = ['colonia', 'zip', 'city', 'province'].filter(function (f) { return !this.inputs[f].value; }, this)[0];
        (firstEmpty ? this.inputs[firstEmpty] : this.inputs.interior).focus();
      }
    }

    /* Fields */

    fill(a) {
      var parts = String(a.address1 || '').split(/,\s*Int\.\s*/i);
      this.inputs.street.value = parts[0] || '';
      this.inputs.interior.value = parts[1] || '';
      this.inputs.colonia.value = a.address2 || '';
      this.inputs.zip.value = (a.zip || '').replace(/\D/g, '').slice(0, 5);
      this.inputs.city.value = a.city || '';
      this.inputs.province.value = STATES[a.provinceCode] ? a.provinceCode : '';
    }

    read() {
      var v = function (f) { return this.inputs[f].value.trim().replace(/\s+/g, ' '); }.bind(this);
      var interior = v('interior');
      return {
        address1: v('street') + (interior ? ', Int. ' + interior : ''),
        address2: v('colonia'),
        zip: v('zip'),
        city: v('city'),
        provinceCode: this.inputs.province.value
      };
    }

    validate() {
      var bad = [];
      var msg = 'Completa los campos marcados.';
      var street = this.inputs.street.value.trim();
      if (!street) bad.push('street');
      else if (!/\d|s\/?n/i.test(street)) { bad.push('street'); msg = 'Agrega tu número exterior a la calle (o “S/N” si no tiene).'; }
      if (!this.inputs.colonia.value.trim()) bad.push('colonia');
      if (!/^\d{5}$/.test(this.inputs.zip.value)) {
        bad.push('zip');
        if (this.inputs.zip.value && bad.length === 1) msg = 'El código postal debe tener 5 dígitos.';
      }
      if (!this.inputs.city.value.trim()) bad.push('city');
      if (!this.inputs.province.value) bad.push('province');
      if (bad.length) this.showError(msg, bad);
      else this.clearError();
      return !bad.length;
    }

    showError(msg, fields) {
      this.clearError();
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
      (fields || []).forEach(function (f) {
        this.inputs[f].classList.add('is-invalid');
        this.inputs[f].setAttribute('aria-invalid', 'true');
      }, this);
    }

    clearError() {
      this.errorEl.hidden = true;
      for (var f in this.inputs) {
        this.inputs[f].classList.remove('is-invalid');
        this.inputs[f].removeAttribute('aria-invalid');
      }
    }

    /* Steps */

    focusSearch() {
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.search.focus({ preventScroll: true });
    }

    showSearch() {
      this.setStep('search');
      this.search.value = '';
      this.focusSearch();
    }

    showManual() {
      this.setStep('fields');
      this.clearError();
      this.querySelector('.mx-address__research').hidden = !!this._googleDown;
      if (!this.inputs.street.value) this.inputs.street.focus();
    }

    showFields() {
      this.setStep('fields');
      this.clearError();
      this.querySelector('.mx-address__research').hidden = !!this._googleDown;
    }

    showSummary() {
      var a = this.read();
      this.summaryText.textContent = [a.address1, a.address2, (a.zip + ' ' + a.city).trim(), STATES[a.provinceCode]]
        .filter(Boolean).join(', ');
      this.setStep('summary');
    }

    save() {
      if (!this.validate()) return Promise.resolve(false);
      var addr = this.read();
      this.classList.add('is-busy');
      return MxCart.save(addr).then(function () {
        this.saved = JSON.stringify(addr);
        this.showSummary();
        return true;
      }.bind(this)).catch(function () {
        // Couldn't store it on the cart; let the customer continue anyway.
        this.showSummary();
        return true;
      }.bind(this)).then(function (ok) {
        this.classList.remove('is-busy');
        return ok;
      }.bind(this));
    }

    /* Checkout gate: resolves true when it's OK to go to checkout. */
    ensureForCheckout() {
      if (this.state === 'summary') return Promise.resolve(true);
      if (this.state === 'fields') {
        return this.save().then(function (ok) {
          if (!ok) this.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return ok;
        }.bind(this));
      }
      if (this._googleDown || !this.isConnected) return Promise.resolve(true);
      this.gateEl.hidden = false;
      this.focusSearch();
      return Promise.resolve(false);
    }

    skip() {
      try { sessionStorage.setItem(SKIP_KEY, '1'); } catch (e) {}
      this.gateEl.hidden = true;
      var btn = document.querySelector('#cartForm [name="checkout"], button[name="checkout"][form="cartForm"]');
      if (btn && btn.form) btn.form.requestSubmit(btn);
    }
  }

  if (!customElements.get('mx-address')) customElements.define('mx-address', MxAddress);

  /* ---------- Gate every "checkout" submit (cart page + added-to-cart popup) ---------- */

  if (!cfg.require) return;

  document.addEventListener('submit', function (e) {
    var form = e.target;
    var btn = e.submitter;
    if (!btn || btn.name !== 'checkout') return;
    if (form.dataset.mxAddrOk === '1') { delete form.dataset.mxAddrOk; return; }
    if (skipped()) return;

    e.preventDefault();
    var proceed = function () {
      form.dataset.mxAddrOk = '1';
      form.requestSubmit(btn);
    };
    var widget = document.querySelector('mx-address');
    var check = widget
      ? widget.ensureForCheckout()
      : MxCart.getAddress().then(function (a) {
          if (a) return true;
          window.location.href = ROOT + 'cart#envio';
          return false;
        });
    check.then(function (ok) { if (ok) proceed(); }, proceed);
  }, true);
})();
